// src/modules/helpers/TableItemsList.js
import ColorPickerModal from "../modals/ColorPickerModal";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Button, Modal, Dropdown } from "react-bootstrap";
import { IonIcon } from "@ionic/react";
import { chevronDown, chevronForward, downloadOutline } from "ionicons/icons";
import TableListItem from "./tableListItem";
import { useLanguage } from "./LanguageContext";
import MillingDropdown from "./MillingSelector";
import "../../css/TableItemsList.css";

// ── Color Swatch gomb ─────────────────────────────────────────────────────────
function SwatchDisplay({ color }) {
  return color.imageDataReduced ? (
    <img
      src={`data:image/jpeg;base64,${color.imageDataReduced}`}
      alt={color.name}
      className="table-items-list__swatch-image"
    />
  ) : (
    <div
      className="table-items-list__swatch-color"
      style={{ "--table-items-list-swatch-color": color.hex || "#ddd" }}
    />
  );
}
function useMediaQuery(query) {
  const [matches, setMatches] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);

    update();

    if (media.addEventListener) {
      media.addEventListener("change", update);
      return () => media.removeEventListener("change", update);
    }

    media.addListener(update);
    return () => media.removeListener(update);
  }, [query]);

  return matches;
}
// ── Fő komponens ──────────────────────────────────────────────────────────────
export default function TableItemsList({
  generatedItems,
  palette,
  tables,
  colors,
  selectedColorId = null,
  handleItemChange,
  handleItemColorChange,
  onDelete,
  onRemoveColor,
  placementItemId = null,
  placementRotation = null,
  canPlaceItems = false,
  onRequestPlacement,
  readOnly = false
}) {
  const { t } = useLanguage();
  const [collapsedColors, setCollapsedColors] = React.useState({});
  const [collapsedTables, setCollapsedTables] = React.useState({});
  const [selectedItems, setSelectedItems] = React.useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTargetItems, setPickerTargetItems] = useState([]);
  const millings = useSelector((state) => state.millings);
  const [rotationConfirmItem, setRotationConfirmItem] = useState(null);
  const allColors = useSelector((state) => state.colors);
  const isTabletLayout = useMediaQuery(
    "(min-width: 768px) and (max-width: 1300px)"
  );
  const toggleColor = (cid) =>
    setCollapsedColors((p) => ({ ...p, [cid]: !p[cid] }));

  const toggleTable = (key) =>
    setCollapsedTables((p) => ({ ...p, [key]: !p[key] }));
  const getEffectiveItems = (items) => {
    const selected = items.filter((it) => selectedItems[it.__idx]);
    return selected.length > 0 ? selected : items;
  };
  const getTargetIndexes = (currentIdx) => {
    const selectedIdxs = Object.keys(selectedItems).map(Number);
    return selectedIdxs.length > 0 ? selectedIdxs : [currentIdx];
  };

  const getAllSelectedIndexes = () => {
    return Object.keys(selectedItems).map(Number);
  };
  const handleCheckedChange = (idx, value) => {
    setSelectedItems((prev) => {
      const next = { ...prev };
      if (value) next[idx] = true;
      else delete next[idx];
      return next;
    });
  };
  const openColorPickerForItems = (items) => {
    setPickerTargetItems(items.map((it) => it.__idx));
    setPickerOpen(true);
  };

  const isAllSelected = (items) => items.every((it) => selectedItems[it.__idx]);

  const toggleSelectAll = (items) => {
    const allSelected = isAllSelected(items);

    setSelectedItems((prev) => {
      const next = { ...prev };

      items.forEach((it) => {
        if (allSelected) {
          delete next[it.__idx];
        } else {
          next[it.__idx] = true;
        }
      });

      return next;
    });
  };

  const getItemColorId = React.useCallback((item) => {
    return item?.color?.colorId ?? item?.colorId ?? null;
  }, []);

  const getQtyForTable = React.useCallback((item, tableId) => {
    if (!item?.tablePosition) return 0;

    const matches = item.tablePosition.match(/\[(.*?)\]/g) || [];

    return matches.filter((block) => {
      const parts = block.replace(/[\[\]]/g, "").split(",");
      return Number(parts[3]) === Number(tableId);
    }).length;
  }, []);

  const getValidTableIds = React.useCallback(
    (item) => {
      if (!item?.tablePosition) return [];

      const matches = item.tablePosition.match(/\[(.*?)\]/g) || [];

      const existingTableIds = new Set(
        (tables || []).map((table) =>
          String(Number(table.id ?? table.tableIndex))
        )
      );

      const itemTableIds = matches
        .map((block) => {
          const parts = block.replace(/[\[\]]/g, "").split(",");
          const tableId = Number(parts[3]);

          return Number.isFinite(tableId) ? String(tableId) : null;
        })
        .filter((tableId) => tableId !== null && existingTableIds.has(tableId));

      return [...new Set(itemTableIds)];
    },
    [tables]
  );

  const getValidPositionCount = React.useCallback(
    (item) => {
      const existingTableIds = new Set(
        (tables || []).map((table) =>
          String(Number(table.id ?? table.tableIndex))
        )
      );

      /*
       * Ha van processedPositions, abból számolunk.
       */
      if (Array.isArray(item?.processedPositions)) {
        return item.processedPositions.filter((position) =>
          existingTableIds.has(String(Number(position.id)))
        ).length;
      }

      /*
       * Egyébként a tablePosition stringből számolunk.
       */
      if (!item?.tablePosition) return 0;

      const matches = item.tablePosition.match(/\[(.*?)\]/g) || [];

      return matches.filter((block) => {
        const parts = block.replace(/[\[\]]/g, "").split(",");
        const tableId = Number(parts[3]);

        return (
          Number.isFinite(tableId) && existingTableIds.has(String(tableId))
        );
      }).length;
    },
    [tables]
  );

  /*
   * Ha a szülő nem ad selectedColorId-t, akkor az első olyan színt
   * használjuk, amely valamelyik elemen szerepel.
   */
  const effectiveColorId = React.useMemo(() => {
    if (selectedColorId != null) {
      return String(selectedColorId);
    }

    const firstColoredItem = (generatedItems || []).find(
      (item) => getItemColorId(item) != null
    );

    if (firstColoredItem) {
      return String(getItemColorId(firstColoredItem));
    }

    const firstPaletteColor = (palette || []).find(
      (color) => color?.colorId != null
    );

    return firstPaletteColor ? String(firstPaletteColor.colorId) : null;
  }, [selectedColorId, generatedItems, palette, getItemColorId]);
  React.useEffect(() => {
    setSelectedItems({});
    setCollapsedColors({});
    setCollapsedTables({});
  }, [effectiveColorId]);
  const effectiveColor = React.useMemo(() => {
    return (
      (palette || []).find(
        (color) => String(color.colorId) === effectiveColorId
      ) || null
    );
  }, [palette, effectiveColorId]);

  /*
   * Fontos, hogy a __idx még a szűrés előtt kerüljön az elemre,
   * így a módosítás mindig az eredeti generatedItems indexét kapja.
   *
   * Mindig látható:
   *  - minden szín nélküli elem;
   *  - az aktuális színhez tartozó elemek.
   *
   * Más színű elemek nem jelennek meg.
   */
  const visibleItems = React.useMemo(() => {
    return (generatedItems || [])
      .filter(Boolean)
      .map((item, originalIndex) => ({
        ...item,
        __idx: originalIndex
      }))
      .filter((item) => {
        const colorId = getItemColorId(item);

        if (colorId == null) {
          return true;
        }

        return (
          effectiveColorId != null &&
          String(colorId) === String(effectiveColorId)
        );
      });
  }, [generatedItems, effectiveColorId, getItemColorId]);

  /*
   * 1. Szín nélküli elemek: mindig legfelül, piros háttérrel.
   */
  const noColorItems = React.useMemo(() => {
    return visibleItems.filter((item) => getItemColorId(item) == null);
  }, [visibleItems, getItemColorId]);

  /*
   * 2. Van színük, de egyetlen létező táblán sincsenek:
   *    a piros blokk után, sárga háttérrel.
   */
  const noTableItems = React.useMemo(() => {
    return visibleItems
      .filter((item) => getItemColorId(item) != null)
      .map((item) => {
        const totalQty = Math.max(0, Number(item.qty ?? 0));
        const placedQty = getValidPositionCount(item);
        const missingQty = Math.max(0, totalQty - placedQty);

        return {
          ...item,

          /*
           * A nincs-tábla listában csak a hiányzó mennyiséget
           * jelenítjük meg.
           */
          qty: missingQty
        };
      })
      .filter((item) => item.qty > 0);
  }, [visibleItems, getItemColorId, getValidPositionCount]);

  /*
   * 3. Az aktuális színhez tartozó, táblára helyezett elemek.
   */
  const tableItems = React.useMemo(() => {
    return visibleItems.filter((item) => {
      const hasColor = getItemColorId(item) != null;
      const placedQty = getValidPositionCount(item);

      return hasColor && placedQty > 0;
    });
  }, [visibleItems, getItemColorId, getValidPositionCount]);

  const itemsTree = React.useMemo(() => {
    return tableItems.reduce((acc, item) => {
      const rawCid = getItemColorId(item);
      const cid = String(rawCid);

      const rawMid = item.milling?.millingId ?? item.millingId;
      const mid = rawMid != null ? String(rawMid) : "no-milling";

      const groupKey = `${cid}__${mid}`;
      const tableIds = getValidTableIds(item);

      acc[groupKey] = acc[groupKey] || {};

      tableIds.forEach((tableId) => {
        acc[groupKey][tableId] = acc[groupKey][tableId] || [];

        acc[groupKey][tableId].push({
          ...item,
          qty: getQtyForTable(item, tableId)
        });
      });

      return acc;
    }, {});
  }, [tableItems, getItemColorId, getValidTableIds, getQtyForTable]);

  const tableLabel = (tableId, table) =>
    table ? `${t("table")} ${tableId}` : t("noTable");
  const itemCanRotate = (item) => {
    return item?.rotable !== false && item?.rotatable !== false;
  };

  const requestPlacement = (
    item,
    index,
    rotation,
    forceRotationAllowed = false
  ) => {
    if (!canPlaceItems) {
      console.warn(
        "Nincs kiválasztott aktuális tábla, ezért nem lehet elhelyezni."
      );
      return;
    }
    onRequestPlacement?.({
      ...item,
      __idx: index,
      placementRotation: rotation,
      forceRotationAllowed
    });
  };

  const handlePlacementChoice = (item, index, rotation) => {
    if (!canPlaceItems) {
      console.warn(
        "Nincs kiválasztott aktuális tábla, ezért nem lehet elhelyezni."
      );
      return;
    }
    if (rotation === 1 && !itemCanRotate(item)) {
      setRotationConfirmItem({
        item,
        index
      });

      return;
    }

    requestPlacement(item, index, rotation, false);
  };
  // Összefüggő sorblokk
  const renderItems = (items, rowVariant = null) => (
    <div className="table-items-list__items-block">
      {items.map(({ __idx, ...itm }, i) => {
        const showPlacementButton =
          !readOnly && (rowVariant === "no-table" || rowVariant === "no-color");

        const isPlacementSelected =
          placementItemId != null &&
          String(placementItemId) === String(itm.itemId);

        return (
          <div
            key={`${itm.itemId || "tmp"}-${__idx}`}
            className={[
              "table-items-list__item-row",
              i === 0 && "table-items-list__item-row--first",
              selectedItems[__idx] && "table-items-list__item-row--selected",
              rowVariant && `table-items-list__item-row--${rowVariant}`,
              isPlacementSelected &&
                "table-items-list__item-row--placement-selected"
            ]
              .filter(Boolean)
              .join(" ")}
            style={
              isPlacementSelected
                ? {
                    backgroundColor: "#d1e7dd",
                    boxShadow: "inset 4px 0 0 #198754"
                  }
                : undefined
            }
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: "8px"
              }}
            >
              {showPlacementButton && (
                <Dropdown
                  align="end"
                  className="table-items-list__placement-dropdown"
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <Dropdown.Toggle
                    as="button"
                    type="button"
                    disabled={!canPlaceItems}
                    aria-label="Elem elhelyezése"
                    aria-pressed={isPlacementSelected}
                    title={
                      canPlaceItems
                        ? isPlacementSelected
                          ? t("cancelPlacement")
                          : t("placeInWaste")
                        : t("selectTableFirst")
                    }
                    className={[
                      "table-items-list__placement-button",
                      isPlacementSelected &&
                        "table-items-list__placement-button--selected"
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <IonIcon
                      icon={downloadOutline}
                      className="table-items-list__placement-icon"
                    />
                  </Dropdown.Toggle>

                  <Dropdown.Menu
                    renderOnMount
                    popperConfig={{ strategy: "fixed" }}
                  >
                    <Dropdown.Item
                      as="button"
                      type="button"
                      active={
                        isPlacementSelected && Number(placementRotation) === 0
                      }
                      onClick={(event) => {
                        event.stopPropagation();
                        handlePlacementChoice(itm, __idx, 0);
                      }}
                    >
                      {t("horizontal")}
                    </Dropdown.Item>

                    <Dropdown.Item
                      as="button"
                      type="button"
                      active={
                        isPlacementSelected && Number(placementRotation) === 1
                      }
                      onClick={(event) => {
                        event.stopPropagation();
                        handlePlacementChoice(itm, __idx, 1);
                      }}
                    >
                      {t("vertical")}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
              <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                <TableListItem
                  Item={itm}
                  index={__idx}
                  readOnly={readOnly}
                  checked={!!selectedItems[__idx]}
                  onCheckedChange={(value) => handleCheckedChange(__idx, value)}
                  onItemChange={(updates) => {
                    if (readOnly) return;

                    getTargetIndexes(__idx).forEach((targetIndex) =>
                      handleItemChange(targetIndex, updates)
                    );
                  }}
                  onItemColorChange={(color) => {
                    if (readOnly) return;

                    getTargetIndexes(__idx).forEach((targetIndex) =>
                      handleItemColorChange(targetIndex, color)
                    );
                  }}
                  onDelete={() => {
                    if (readOnly) return;

                    const selectedIndexes = getAllSelectedIndexes();
                    let ids = [];

                    if (selectedIndexes.length > 0) {
                      ids = items
                        .filter((item) => selectedItems[item.__idx])
                        .map((item) => item.itemId)
                        .filter(Boolean);
                    } else {
                      const item = items.find((item) => item.__idx === __idx);

                      if (item) ids = [item.itemId];
                    }

                    onDelete(ids);
                  }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderTables = (groupKey) =>
    Object.entries(itemsTree[groupKey] || {}).map(([tableId, items]) => {
      const key = `${groupKey}_${tableId}`;
      const table =
        tables?.find((tb) => String(tb.id ?? tb.tableIndex) === tableId) ||
        null;

      return (
        <div key={key}>
          <div className="table-items-list__table-header">
            <input
              type="checkbox"
              checked={isAllSelected(items)}
              onChange={(e) => {
                e.stopPropagation();
                toggleSelectAll(items);
              }}
              className="table-items-list__checkbox"
            />

            <IonIcon
              icon={collapsedTables[key] ? chevronForward : chevronDown}
              onClick={() => toggleTable(key)}
              className="table-items-list__chevron"
            />

            <strong className="table-items-list__table-title">
              {tableLabel(tableId, table)}
            </strong>
          </div>

          {!collapsedTables[key] && renderItems(items)}
        </div>
      );
    });

  const sortedGroups = Object.entries(itemsTree).sort(([aKey], [bKey]) => {
    const [, aMid] = aKey.split("__");
    const [, bMid] = bKey.split("__");

    const aMilling = millings.find(
      (milling) => String(milling.millingId) === aMid
    );

    const bMilling = millings.find(
      (milling) => String(milling.millingId) === bMid
    );

    const aName = aMilling?.name || "";
    const bName = bMilling?.name || "";

    return aName.localeCompare(bName, "hu");
  });

  return (
    <div
      className={[
        "table-items-list",
        isTabletLayout
          ? "table-items-list--tablet"
          : "table-items-list--desktop"
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* ========== HAS COLOR, BUT NO TABLE ========== */}
      {noTableItems.length > 0 && (
        <div className="table-items-list__bucket table-items-list__bucket--no-table">
          <div className="table-items-list__bucket-header table-items-list__bucket-header--no-table">
            <div className="table-items-list__bucket-left">
              <input
                type="checkbox"
                checked={isAllSelected(noTableItems)}
                onChange={(event) => {
                  event.stopPropagation();
                  toggleSelectAll(noTableItems);
                }}
                className="table-items-list__checkbox"
              />

              <div
                onClick={() => toggleColor("no-table")}
                className="table-items-list__bucket-toggle"
              >
                <IonIcon
                  icon={
                    collapsedColors["no-table"] ? chevronForward : chevronDown
                  }
                />

                <strong>
                  {t("noTable")}
                  {effectiveColor ? ` – ${t(effectiveColor.name)}` : ""}
                </strong>
              </div>
            </div>
          </div>

          {!collapsedColors["no-table"] &&
            renderItems(noTableItems, "no-table")}
        </div>
      )}
      {/* ========== NO COLOR BUCKET ========== */}
      {noColorItems.length > 0 && (
        <div className="table-items-list__bucket">
          <div className="table-items-list__bucket-header table-items-list__bucket-header--no-color">
            <div className="table-items-list__bucket-left">
              <input
                type="checkbox"
                checked={isAllSelected(noColorItems)}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleSelectAll(noColorItems);
                }}
                className="table-items-list__checkbox"
              />

              <div
                onClick={() => toggleColor("no-color")}
                className="table-items-list__bucket-toggle"
              >
                <IonIcon
                  icon={
                    collapsedColors["no-color"] ? chevronForward : chevronDown
                  }
                />
                <strong>{t("noColor")}</strong>
              </div>
            </div>

            {!readOnly && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openColorPickerForItems(getEffectiveItems(noColorItems));
                }}
                className="table-items-list__add-color-button"
              >
                + {t("addColor")}
              </button>
            )}
          </div>
          {!collapsedColors["no-color"] &&
            renderItems(noColorItems, "no-color")}{" "}
        </div>
      )}

      {/* ========== COLOR BUCKETS ========== */}
      {sortedGroups.map(([groupKey, tablesMap]) => {
        const [cid, mid] = groupKey.split("__");

        const color = palette.find((c) => String(c.colorId) === cid);
        if (!color) return null;

        const itemsForColor = Object.values(tablesMap).flat();
        if (itemsForColor.length === 0) return null;

        const milling =
          millings.find((m) => String(m.millingId) === mid) || null;

        const isCollapsed = !!collapsedColors[groupKey];

        const tableIds = Object.keys(tablesMap).filter(
          (tid) => tid !== "no-table"
        );

        const tableCount = tableIds.length;

        const firstTable = tables?.find((tb) =>
          tableIds.includes(String(tb.id ?? tb.tableIndex))
        );
        const commonMillingId = mid !== "no-milling" ? Number(mid) : null;
        const tableUsage = firstTable?.tableUsage ?? 0;
        const isMDF = color.materialType === "MDF";

        return (
          <div key={groupKey} className="table-items-list__color-group">
            <div className="table-items-list__color-header">
              <input
                type="checkbox"
                checked={isAllSelected(itemsForColor)}
                onChange={() => toggleSelectAll(itemsForColor)}
                className="table-items-list__checkbox"
              />

              <IonIcon
                icon={isCollapsed ? chevronForward : chevronDown}
                className="table-items-list__chevron table-items-list__chevron--fixed"
                onClick={() => toggleColor(groupKey)}
              />

              <div className="table-items-list__swatch-wrapper">
                {!readOnly && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();

                      getEffectiveItems(itemsForColor).forEach((it) => {
                        handleItemColorChange(it.__idx, { colorId: null });
                      });
                    }}
                    className="table-items-list__remove-color"
                  >
                    ×
                  </div>
                )}

                <div
                  onClick={() =>
                    !readOnly &&
                    openColorPickerForItems(getEffectiveItems(itemsForColor))
                  }
                  className="table-items-list__swatch"
                >
                  <SwatchDisplay color={color} />
                </div>
              </div>

              <div className="table-items-list__color-content">
                <strong
                  className="table-items-list__color-title"
                  onClick={() => toggleColor(groupKey)}
                >
                  {isMDF
                    ? `${tableUsage} m² - `
                    : tableCount > 0
                      ? `(${tableCount}) `
                      : ""}
                  {t(color.name)}
                </strong>

                <div className="table-items-list__milling">
                  <MillingDropdown
                    millingId={commonMillingId}
                    colorId={color.colorId}
                    allColors={allColors}
                    allMillings={millings}
                    disabled={readOnly}
                    onChange={(patch) => {
                      const targetItems = getEffectiveItems(itemsForColor);

                      targetItems.forEach((it) => {
                        handleItemChange(it.__idx, {
                          milling: patch.millingId
                            ? { millingId: patch.millingId }
                            : null,
                          millingVariantId: patch.millingVariantId || "",
                          millingId: patch.millingId ?? null
                        });
                      });
                    }}
                  />
                </div>
              </div>
            </div>

            {!isCollapsed && renderTables(groupKey)}
          </div>
        );
      })}
      <ColorPickerModal
        show={pickerOpen}
        colors={colors}
        millings={millings}
        onSelectMilling={(milling, variantId, selectedColor) => pickerTargetItems.forEach((idx) => handleItemChange(idx, {
          milling: milling ? { millingId: milling.millingId } : null,
          ...(selectedColor ? { color: { colorId: selectedColor.colorId } } : {}),
          millingId: milling?.millingId ?? null, millingVariantId: variantId || ""
        }))}
        onSelect={(selectedColor) => {
          pickerTargetItems.forEach((idx) =>
            handleItemColorChange(idx, selectedColor)
          );
          setPickerOpen(false);
        }}
        onClose={() => setPickerOpen(false)}
      />
      <Modal
        show={rotationConfirmItem != null}
        onHide={() => setRotationConfirmItem(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("enableRotation")}</Modal.Title>
        </Modal.Header>

        <Modal.Body>{t("enableRotationConfirm")}</Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setRotationConfirmItem(null)}
          >
            {t("cancel")}
          </Button>

          <Button
            variant="primary"
            onClick={() => {
              const pending = rotationConfirmItem;

              if (!pending) return;

              setRotationConfirmItem(null);

              requestPlacement(pending.item, pending.index, 1, true);
            }}
          >
            {t("confirmRotation")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
